/**
 * Texter och små beräkningar för Möten. Ingen JSX här – filen delas av flera
 * komponenter och får inte bryta Fast Refresh.
 */
import { FEL_UTAN_NYTT_FORSOK } from '../../../../netlify/functions/_shared/moten/texter';
import type { Kalla, Mote, MotesTyp, Status } from '../../../../netlify/functions/_shared/moten/typer';

export const INBJUDNINGSRAD = 'Mötet antecknas av ELvis Hub. Säg till i början om du inte vill att det spelas in.';
export const BOTNAMN = 'ELvis Hub – anteckningar';

export const MAX_FIL_BYTE = 500 * 1024 * 1024;

/** Filändelse → ren MIME-typ för uppladdningen. Bucketen tar audio/* samt video/mp4 och video/webm. */
export const FIL_MIME: Record<string, string> = {
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  wav: 'audio/wav',
  webm: 'audio/webm',
  mp4: 'video/mp4',
};

export const TYP_ETIKETT: Record<MotesTyp, string> = {
  intern_alla: 'Internt – alla',
  intern_begransad: 'Internt – begränsat',
  kund: 'Kundmöte',
};

export const TYP_FORKLARING: Record<MotesTyp, string> = {
  intern_alla: 'Alla på El-kretsen kan läsa mötet.',
  intern_begransad: 'Bara du och de du lägger till kan läsa mötet.',
  kund: 'Bara du och de du lägger till. Läggs aldrig i kunskapsbasen och raderas helt efter 12 månader.',
};

export const KALLA_ETIKETT: Record<Kalla, string> = {
  klang: 'Klang',
  uppladdning: 'Uppladdad fil',
  inspelning: 'Inspelning',
};

/**
 * Kort status för listan. `fel` på ett möte som inte är i status fel är en
 * synlig notis (t.ex. utskrift av filer inte påslagen) – då lovar pillret inget.
 */
export function statusKort(status: Status, kalla: Kalla, fel: string | null = null): string {
  switch (status) {
    case 'bokad':
      return 'Bokad';
    case 'bot_skickad':
      return 'Boten är i mötet';
    case 'vantar_ljud':
      return kalla === 'inspelning' ? 'Spelas in' : 'Väntar på filen';
    case 'ljud_mottaget':
      return fel ? 'Väntar på utskrift' : 'Ljudet har kommit';
    case 'vantar_utskrift':
      return 'Skrivs ut';
    case 'utskrift_klar':
      return 'Sammanfattas';
    case 'sammanfattad':
      return 'Klart';
    case 'fel':
      return 'Fel';
  }
}

/** Pågår något som sidan väntar på? (Styr omhämtningen.) */
export function arPagaende(status: Status): boolean {
  return status !== 'sammanfattad' && status !== 'fel';
}

const POLL_MS = 10_000;
/** Utan tal-till-text händer inget förrän den slås på – sidan frågar mer sällan. */
const POLL_UTAN_STT_MS = 60_000;

/**
 * Hur ofta mötesvyn hämtar om mötet: null när inget pågår, var 60:e sekund när
 * ljudet väntar på en utskrift som inte är påslagen, annars var 10:e sekund.
 */
export function pollMs(status: Status, sttPa: boolean | undefined): number | null {
  if (!arPagaende(status)) return null;
  return status === 'ljud_mottaget' && sttPa === false ? POLL_UTAN_STT_MS : POLL_MS;
}

export type FelAtgard = 'forsok_igen' | 'skicka_in_nu' | 'radera';

/**
 * Vad ägaren kan göra med ett möte i status fel – bara det som leder framåt:
 *  - Klang utan utskrift: "Skicka in boten nu" (försök igen går till bokad, och
 *    ticken ger då genast "för sent" för ett möte som redan börjat),
 *  - utan utskrift och utan användbart ljud: bara radera (försök igen ger samma
 *    fel igen; texten hänvisar till ett nytt möte),
 *  - annars "Försök igen".
 */
export function felAtgarder(m: Pick<Mote, 'status' | 'kalla' | 'fel' | 'utskrift' | 'ljud_path' | 'motes_lank'>): FelAtgard[] {
  if (m.status !== 'fel') return [];
  if (m.utskrift && m.utskrift.length > 0) return ['forsok_igen'];
  if (m.kalla === 'klang') return m.motes_lank ? ['skicka_in_nu'] : ['forsok_igen'];
  if (!m.ljud_path || (m.fel !== null && FEL_UTAN_NYTT_FORSOK.includes(m.fel))) return ['radera'];
  return ['forsok_igen'];
}

const STOCKHOLM = 'Europe/Stockholm';

export function datumText(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: STOCKHOLM,
  });
}

export function tidText(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: STOCKHOLM,
  });
}

export function datumTidText(iso: string | null): string {
  if (!iso) return '';
  return `${datumText(iso)} kl. ${tidText(iso)}`;
}

/** Sekunder → "4:07" eller "1:04:07". */
export function klockText(sek: number): string {
  const s = Math.max(0, Math.floor(sek));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

export function storlekText(byte: number): string {
  if (byte >= 1024 * 1024 * 1024)
    return `${(byte / (1024 * 1024 * 1024)).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} GB`;
  if (byte >= 1024 * 1024) return `${(byte / (1024 * 1024)).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} MB`;
  return `${Math.max(1, Math.round(byte / 1024))} kB`;
}

export function antal(n: number, en: string, flera: string): string {
  return `${n} ${n === 1 ? en : flera}`;
}

export interface StatusBesked {
  rubrik: string;
  text: string;
}

/**
 * Status i klartext för mötesvyn. En `fel`-text på ett möte som inte är i status
 * fel är en notis från servern (t.ex. att utskrift inte är påslagen, eller att
 * sammanfattningen tas om) och visas som den står i stället för standardtexten.
 */
export function statusBesked(m: Mote, nu: Date): StatusBesked {
  const b = grundBesked(m, nu);
  if (m.fel && m.status !== 'fel' && m.status !== 'sammanfattad') {
    if (m.status === 'ljud_mottaget') {
      return { rubrik: b.rubrik, text: `${m.fel} Ljudet är sparat och mötet skrivs ut när det är påslaget.` };
    }
    return { rubrik: b.rubrik, text: m.fel };
  }
  return b;
}

function grundBesked(m: Mote, nu: Date): StatusBesked {
  switch (m.status) {
    case 'bokad': {
      if (!m.starttid) {
        return {
          rubrik: 'Bokad – väntar på att du skickar in boten',
          text: `Ingen starttid är satt. Tryck Skicka in nu när mötet har börjat, och släpp in "${BOTNAMN}" när den knackar på.`,
        };
      }
      const start = new Date(m.starttid);
      if (start.getTime() > nu.getTime()) {
        return {
          rubrik: 'Väntar på att mötet börjar',
          text: `Boten "${BOTNAMN}" går in i mötet ${datumTidText(m.starttid)}. Släpp in den när den knackar på.`,
        };
      }
      return {
        rubrik: 'Boten är på väg in',
        text: `Starttiden ${tidText(m.starttid)} har passerat. Boten skickas in inom någon minut – har den inte kommit kan du skicka in den nu.`,
      };
    }
    case 'bot_skickad':
      return {
        rubrik: 'Boten är i mötet',
        text: `"${BOTNAMN}" har skickats in. Väntar den i lobbyn behöver någon släppa in den. Utskriften kommer när mötet är slut.`,
      };
    case 'vantar_ljud':
      return m.kalla === 'inspelning'
        ? {
            rubrik: 'Inspelningen är inte avslutad',
            text: 'Pågår den i en annan flik eller på en annan enhet – avsluta den där. Stängdes fliken finns det som hann laddas upp kvar, och du kan avsluta inspelningen här.',
          }
        : {
            rubrik: 'Väntar på ljudfilen',
            text: 'Filen har inte kommit fram. Blev uppladdningen klar kan du fortsätta härifrån; annars raderar du mötet och laddar upp filen på nytt.',
          };
    case 'ljud_mottaget':
      return {
        rubrik: 'Ljudet har kommit',
        text: 'Utskriften startar inom ett par minuter. Sidan uppdateras av sig själv.',
      };
    case 'vantar_utskrift':
      return {
        rubrik: 'Skrivs ut',
        text: 'Ljudet skrivs ut till text. Sidan uppdateras av sig själv.',
      };
    case 'utskrift_klar':
      return {
        rubrik: 'Sammanfattas',
        text: 'Utskriften är klar och sammanfattningen skrivs. Sidan uppdateras av sig själv.',
      };
    case 'sammanfattad':
      return {
        rubrik: 'Klart',
        text: m.klar_at ? `Sammanfattat ${datumTidText(m.klar_at)}.` : 'Utskrift och sammanfattning är klara.',
      };
    case 'fel':
      return {
        rubrik: 'Något gick fel',
        text: m.fel ?? 'Mötet stannade utan förklaring.',
      };
  }
}

/** Läsbar text ur ett fel från motenApi eller nätet. */
export function felText(e: unknown, vad = 'Det'): string {
  if (e instanceof TypeError) return 'Kunde inte nå ELvis Hub. Kontrollera uppkopplingen och försök igen.';
  const err = e as { status?: number; serverText?: string; message?: string };
  if (err?.status === 401) return 'Din inloggning har gått ut. Ladda om sidan och logga in igen.';
  if (err?.serverText) return err.serverText;
  if (err?.status && err.status >= 500) return `${vad} gick inte just nu (${err.status}). Försök igen om en stund.`;
  if (err?.message && !/^HTTP \d+$/.test(err.message)) return err.message;
  return `${vad} gick inte. Försök igen.`;
}
